import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from '../../components/ui/table';
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger 
} from '../../components/ui/tabs';
import { 
  ArrowDownCircle, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  DollarSign,
  User,
  Check,
  X,
  CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWithdrawalRequestsPage() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, processed: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionModal, setActionModal] = useState({ show: false, type: null });
  const [actionData, setActionData] = useState({ notes: '', amount: '', reference: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [token, activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const status = activeTab !== 'all' ? activeTab : null;
      const params = status ? `?status=${status}` : '';
      const response = await axios.get(`${API_URL}/api/admin/withdrawal-requests${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.items);
      setCounts(response.data.counts);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/withdrawal-requests/${selectedRequest.id}/approve`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { admin_notes: actionData.notes || null }
        }
      );
      toast.success('Request approved');
      fetchRequests();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !actionData.notes) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/withdrawal-requests/${selectedRequest.id}/reject`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { admin_notes: actionData.notes }
        }
      );
      toast.success('Request rejected');
      fetchRequests();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcess = async () => {
    if (!selectedRequest || !actionData.amount || !actionData.reference) {
      toast.error('Please provide amount and reference number');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/withdrawal-requests/${selectedRequest.id}/process`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            processed_amount: parseFloat(actionData.amount),
            reference_number: actionData.reference,
            admin_notes: actionData.notes || null
          }
        }
      );
      toast.success('Request processed');
      fetchRequests();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessing(false);
    }
  };

  const openModal = (request, type) => {
    setSelectedRequest(request);
    setActionModal({ show: true, type });
    setActionData({
      notes: '',
      amount: request.requested_amount?.toString() || '',
      reference: ''
    });
  };

  const closeModal = () => {
    setActionModal({ show: false, type: null });
    setSelectedRequest(null);
    setActionData({ notes: '', amount: '', reference: '' });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      approved: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
      processed: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getTypeLabel = (type) => {
    return {
      interest_distribution: 'Interest Distribution',
      partial_redemption: 'Partial Redemption',
      full_redemption: 'Full Redemption'
    }[type] || type;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 p-6" data-testid="admin-withdrawal-requests-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Withdrawal Requests</h1>
        <p className="text-gray-400 mt-1">Review and process investor withdrawal requests</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">{counts.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Approved</p>
                <p className="text-2xl font-bold text-blue-400">{counts.approved}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Processed</p>
                <p className="text-2xl font-bold text-green-400">{counts.processed}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Rejected</p>
                <p className="text-2xl font-bold text-red-400">{counts.rejected}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Table */}
      <Card className="bg-[#1a1a1a] border-[#2a2a2a]">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="bg-[#2a2a2a]">
              <TabsTrigger value="pending" className="data-[state=active]:bg-[#B8860B]">
                Pending ({counts.pending})
              </TabsTrigger>
              <TabsTrigger value="approved" className="data-[state=active]:bg-[#B8860B]">
                Approved ({counts.approved})
              </TabsTrigger>
              <TabsTrigger value="processed" className="data-[state=active]:bg-[#B8860B]">
                Processed ({counts.processed})
              </TabsTrigger>
              <TabsTrigger value="rejected" className="data-[state=active]:bg-[#B8860B]">
                Rejected ({counts.rejected})
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent className="pt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#B8860B]" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <ArrowDownCircle className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No {activeTab} requests</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2a2a2a]">
                    <TableHead className="text-gray-400">Investor</TableHead>
                    <TableHead className="text-gray-400">Type</TableHead>
                    <TableHead className="text-gray-400">Amount</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id} className="border-[#2a2a2a]">
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{request.investor_name}</p>
                          <p className="text-xs text-gray-400">{request.investor_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {getTypeLabel(request.request_type)}
                      </TableCell>
                      <TableCell className="text-[#B8860B] font-semibold">
                        {formatCurrency(request.processed_amount || request.requested_amount)}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {formatDate(request.created_at)}
                      </TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="text-right">
                        {request.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => openModal(request, 'approve')}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openModal(request, 'reject')}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {request.status === 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => openModal(request, 'process')}
                            className="bg-[#B8860B] hover:bg-[#9A7209]"
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            Process
                          </Button>
                        )}
                        {(request.status === 'processed' || request.status === 'rejected') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openModal(request, 'view')}
                            className="border-[#3a3a3a]"
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Action Modal */}
      <Dialog open={actionModal.show} onOpenChange={closeModal}>
        <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-white">
          <DialogHeader>
            <DialogTitle>
              {actionModal.type === 'approve' && 'Approve Request'}
              {actionModal.type === 'reject' && 'Reject Request'}
              {actionModal.type === 'process' && 'Process Payment'}
              {actionModal.type === 'view' && 'Request Details'}
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="p-4 bg-[#2a2a2a] rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Investor:</span>
                  <span className="text-white">{selectedRequest.investor_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Type:</span>
                  <span className="text-white">{getTypeLabel(selectedRequest.request_type)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-[#B8860B] font-semibold">
                    {formatCurrency(selectedRequest.requested_amount)}
                  </span>
                </div>
                {selectedRequest.notes && (
                  <div>
                    <span className="text-gray-400">Notes:</span>
                    <p className="text-gray-300 mt-1">{selectedRequest.notes}</p>
                  </div>
                )}
              </div>

              {/* Action Form */}
              {actionModal.type === 'process' && (
                <>
                  <div>
                    <label className="text-sm text-gray-400">Amount Processed</label>
                    <Input
                      type="number"
                      value={actionData.amount}
                      onChange={(e) => setActionData(p => ({ ...p, amount: e.target.value }))}
                      className="bg-[#2a2a2a] border-[#3a3a3a] mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Reference Number *</label>
                    <Input
                      value={actionData.reference}
                      onChange={(e) => setActionData(p => ({ ...p, reference: e.target.value }))}
                      placeholder="Bank transfer reference"
                      className="bg-[#2a2a2a] border-[#3a3a3a] mt-1"
                    />
                  </div>
                </>
              )}

              {(actionModal.type === 'approve' || actionModal.type === 'reject' || actionModal.type === 'process') && (
                <div>
                  <label className="text-sm text-gray-400">
                    {actionModal.type === 'reject' ? 'Reason for Rejection *' : 'Admin Notes (Optional)'}
                  </label>
                  <Textarea
                    value={actionData.notes}
                    onChange={(e) => setActionData(p => ({ ...p, notes: e.target.value }))}
                    placeholder={actionModal.type === 'reject' ? 'Please provide a reason...' : 'Additional notes...'}
                    className="bg-[#2a2a2a] border-[#3a3a3a] mt-1"
                  />
                </div>
              )}

              {/* View-only details */}
              {actionModal.type === 'view' && (
                <>
                  {selectedRequest.admin_notes && (
                    <div className="p-3 bg-[#2a2a2a] rounded">
                      <span className="text-gray-400 text-sm">Admin Notes:</span>
                      <p className="text-gray-300">{selectedRequest.admin_notes}</p>
                    </div>
                  )}
                  {selectedRequest.reference_number && (
                    <div className="p-3 bg-[#2a2a2a] rounded">
                      <span className="text-gray-400 text-sm">Reference:</span>
                      <p className="text-white font-mono">{selectedRequest.reference_number}</p>
                    </div>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={closeModal} className="border-[#3a3a3a]">
                  {actionModal.type === 'view' ? 'Close' : 'Cancel'}
                </Button>
                {actionModal.type === 'approve' && (
                  <Button 
                    onClick={handleApprove} 
                    disabled={processing}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Approve'}
                  </Button>
                )}
                {actionModal.type === 'reject' && (
                  <Button 
                    onClick={handleReject} 
                    disabled={processing}
                    variant="destructive"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reject'}
                  </Button>
                )}
                {actionModal.type === 'process' && (
                  <Button 
                    onClick={handleProcess} 
                    disabled={processing}
                    className="bg-[#B8860B] hover:bg-[#9A7209]"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Process Payment'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
